import dayjs, { Dayjs } from "dayjs";
import "dayjs/locale/en-gb";

import { useEffect, useState } from "react";

import { Box } from "@mui/material";
import { GridFilterOperator } from "@mui/x-data-grid";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";

export type DateRangeQueryParam = {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
};

const startTimeReference = dayjs().set("hours", 0).set("minutes", 0).set("seconds", 0);
const endTimeReference = dayjs().set("hours", 23).set("minutes", 59).set("seconds", 0);

const dateRangeFilter = (
  validate: (start?: Date, end?: Date) => boolean,
  getValueAsString: (values: Date[]) => string,
): GridFilterOperator<any, string> => {
  return {
    label: "Between",
    value: "between",
    getApplyFilterFn: ({ value }) =>
      Array.isArray(value) && value.length === 2 && value[0] != null && value[1] != null
        ? (cellValue) => cellValue != null && value[0] <= cellValue && cellValue <= value[1]
        : null,
    InputComponent: (props) => {
      const { item, applyValue } = props;
      const [start, setStart] = useState(item.value?.[0] ?? null);
      const [end, setEnd] = useState(item.value?.[1] ?? null);
      const [isValid, setIsValid] = useState(validate(start?.toDate(), end?.toDate()));

      const handleStartDateChange = (date: Dayjs | null) => {
        const isValid = validate(date?.toDate(), end?.toDate());
        setStart(date);
        setIsValid(isValid);
        applyValue({ ...item, value: [date, end], isValid });
      };

      const handleEndDateChange = (date: Dayjs | null) => {
        const isValid = validate(start?.toDate(), date?.toDate());
        setEnd(date);
        setIsValid(isValid);
        applyValue({ ...item, value: [start, date], isValid });
      };

      useEffect(() => {
        if (item.value === undefined) {
          setStart(null);
          setEnd(null);
          setIsValid(false);
        }
      }, [item.value]);

      return (
        <Box
          sx={{
            alignItems: "center",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              label="Start *"
              value={start}
              ampm={false}
              timeSteps={{ hours: 1, minutes: 1 }}
              onChange={handleStartDateChange}
              referenceDate={startTimeReference}
              format={"YYYY-MM-DD HH:mm"}
              disableFuture
              slotProps={{
                textField: { variant: "standard", error: !isValid, InputLabelProps: { shrink: true } },
              }}
              sx={{ width: { xs: "100%", sm: 190 } }}
            />
            <DateTimePicker
              label="End *"
              value={end}
              ampm={false}
              timeSteps={{ hours: 1, minutes: 1 }}
              onChange={handleEndDateChange}
              referenceDate={endTimeReference}
              format={"YYYY-MM-DD HH:mm"}
              minDate={start}
              slotProps={{
                textField: { variant: "standard", error: !isValid, InputLabelProps: { shrink: true } },
              }}
              sx={{ width: { xs: "100%", sm: 190 }, marginLeft: { xs: 0, sm: 1 } }}
            />
          </LocalizationProvider>
        </Box>
      );
    },
    getValueAsString,
  };
};

export default dateRangeFilter;
